import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sessionService } from "../services/session.service";
import { authService } from "../services/auth.service";
import { UserProfile } from "../components/UserProfile";
import { AuthForm } from "../components/AuthForm";

export function Lobby() {
  const [playerName, setPlayerName] = useState("");
  const [error, setError] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        const isValid = await authService.validateToken();
        setIsAuthenticated(isValid);
        if (isValid) {
          const authUser = authService.getAuthUser();
          if (authUser) {
            setPlayerName(authUser.username);
          }
        }
      } else {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  // Load player name from session if available (for guest mode)
  useEffect(() => {
    if (!isAuthenticated) {
      const session = sessionService.getSession();
      if (session?.playerName || session?.displayName) {
        setPlayerName(session.displayName || session.playerName);
      } else {
        // Ensure user exists (will generate userId if needed)
        const user = sessionService.getOrCreateUser();
        if (user.displayName && user.displayName !== "Player") {
          setPlayerName(user.displayName);
        }
      }
    }
  }, [isAuthenticated]);

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    const authUser = authService.getAuthUser();
    if (authUser) {
      setPlayerName(authUser.username);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!playerName.trim()) {
      setError("Vui lòng nhập tên của bạn");
      return;
    }

    // For authenticated users, use their username
    // For guest users, use provided player name
    if (isAuthenticated) {
      const authUser = authService.getAuthUser();
      if (authUser) {
        // Use authenticated username
        navigate("/rooms", {
          state: { playerName: authUser.username },
        });
        return;
      }
    }

    // Guest mode: ensure user exists and update display name
    const user = sessionService.getOrCreateUser(playerName.trim());
    sessionService.updateDisplayName(playerName.trim());

    // Navigate to room management page with player name
    navigate("/rooms", {
      state: { playerName: playerName.trim() },
    });
  };

  // Show auth form if not authenticated
  if (!isAuthenticated) {
    return <AuthForm onSuccess={handleAuthSuccess} />;
  }

  if (showProfile) {
    return <UserProfile onClose={() => setShowProfile(false)} />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>🃏 UNO Multiplayer</h1>
          <div style={styles.headerButtons}>
            <button
              onClick={async () => {
                await authService.logout();
                setIsAuthenticated(false);
                setPlayerName("");
              }}
              style={styles.logoutButton}
              title="Logout"
            >
              🚪
            </button>
            <button onClick={() => setShowProfile(true)} style={styles.profileButton}>
              👤
            </button>
          </div>
        </div>
        <p style={styles.subtitle}>
          Welcome, {authService.getAuthUser()?.username || playerName}!
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            placeholder="Tên của bạn"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            style={styles.input}
            maxLength={20}
            autoFocus
          />
          <button type="submit" style={styles.buttonPrimary}>
            Tiếp tục
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg)",
    padding: "20px",
  },
  card: {
    background: "var(--bg-card)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    borderRadius: "16px",
    padding: "40px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: "400px",
    width: "100%",
    border: "1px solid var(--border-subtle)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    textAlign: "center",
    color: "var(--text)",
    flex: 1,
  },
  headerButtons: {
    display: "flex",
    gap: "10px",
  },
  profileButton: {
    background: "var(--bg-card-hover)",
    border: "1px solid var(--border-subtle)",
    fontSize: "24px",
    cursor: "pointer",
    padding: "5px",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
    color: "var(--text)",
  },
  logoutButton: {
    background: "var(--bg-card-hover)",
    border: "1px solid var(--border-subtle)",
    fontSize: "24px",
    cursor: "pointer",
    padding: "5px",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
    color: "var(--text)",
  },
  subtitle: {
    margin: "0 0 30px 0",
    textAlign: "center",
    color: "var(--text-muted)",
    fontSize: "16px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  input: {
    padding: "12px 16px",
    fontSize: "16px",
    border: "2px solid var(--border-medium)",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    background: "var(--bg-card-hover)",
    color: "var(--text)",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
    marginTop: "10px",
  },
  buttonPrimary: {
    flex: 1,
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    background: "var(--btn-primary)",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  buttonSecondary: {
    flex: 1,
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#667eea",
    background: "white",
    border: "2px solid #667eea",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  error: {
    padding: "12px",
    background: "color-mix(in srgb, var(--btn-danger) 15%, transparent)",
    color: "var(--btn-danger)",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "14px",
    border: "1px solid color-mix(in srgb, var(--btn-danger) 40%, transparent)",
  },
};
